import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, FileText, Lock, Receipt } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import shareApi, { ShareUnlocked } from "@/types/clinova/shareApi";
import mutate from "@/Utils/request/mutate";
import query, { callApi } from "@/Utils/request/query";

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Plan 32 F7: the patient's download page (public, no login): the lab and
 * the patient's initials; after the year of birth or the last 4 digits of
 * the mobile, the visit's tests with the approved reports and the bill to
 * download.
 */
export default function PatientReports({ code }: { code: string }) {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");
  const [unlocked, setUnlocked] = useState<ShareUnlocked | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["share-summary", code],
    queryFn: query(shareApi.summary, { pathParams: { code }, silent: true }),
    retry: false,
  });
  const { mutate: unlock, isPending } = useMutation({
    mutationFn: mutate(shareApi.unlock, {
      pathParams: { code },
      silent: true,
    }),
    onSuccess: (result: ShareUnlocked) => {
      setUnlocked(result);
      setError("");
    },
    onError: () => {
      setError(t("share_wrong_answer"));
      refetch();
    },
  });

  const download = async (kind: "report" | "bill", id?: string, name = "") => {
    if (!unlocked) return;
    setBusy(id ?? kind);
    try {
      const blob =
        kind === "report"
          ? await callApi(shareApi.report, {
              pathParams: { code, reportId: id ?? "" },
              headers: { "X-Share-Token": unlocked.token },
            })
          : await callApi(shareApi.bill, {
              pathParams: { code },
              headers: { "X-Share-Token": unlocked.token },
            });
      if (blob instanceof Blob) saveBlob(blob, `${name || kind}.pdf`);
    } catch {
      setError(t("share_open_again"));
      setUnlocked(null);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-gray-100 p-4 sm:items-center">
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        data-cy="patient-reports"
      >
        {isLoading ? (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        ) : isError || !data ? (
          <p className="text-sm text-red-700">{t("share_link_not_found")}</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {data.logo && (
                <img src={data.logo} alt="" className="size-12 object-contain" />
              )}
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  {data.lab}
                </div>
                <div className="text-xs text-gray-500">
                  {t("share_reports_for", {
                    patient: unlocked?.patient ?? data.patient_initials,
                  })}
                </div>
              </div>
            </div>
            {data.expired ? (
              <p className="mt-6 text-sm text-red-700">{t("share_expired")}</p>
            ) : !unlocked ? (
              <div className="mt-6 space-y-3">
                <p className="flex items-center gap-2 text-sm text-gray-700">
                  <Lock className="size-4" />
                  {t("share_unlock_hint")}
                </p>
                <Label htmlFor="share-answer" className="sr-only">
                  {t("share_unlock_hint")}
                </Label>
                <Input
                  id="share-answer"
                  inputMode="numeric"
                  maxLength={4}
                  value={answer}
                  onChange={(e) =>
                    setAnswer(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder={t("share_answer_placeholder")}
                  data-cy="share-answer"
                />
                {(error || data.locked) && (
                  <p className="text-sm text-red-700" data-cy="share-error">
                    {data.locked && !error ? t("share_locked") : error}
                  </p>
                )}
                <Button
                  className="w-full"
                  disabled={answer.length !== 4 || isPending}
                  onClick={() => unlock({ answer })}
                  data-cy="share-unlock"
                >
                  {t("share_open_reports")}
                </Button>
              </div>
            ) : (
              <div className="mt-6 space-y-2">
                <p className="text-xs text-gray-500">
                  {t("share_visit_of", { date: unlocked.visit_date })}
                </p>
                {unlocked.tests.map((test) => (
                  <div
                    key={test.title}
                    className="flex items-center justify-between gap-2 rounded-md border border-gray-200 p-2"
                    data-cy="share-test"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="size-4 text-gray-500" />
                      <div>
                        <div className="font-medium">{test.title}</div>
                        <div className="text-xs text-gray-500">
                          {test.ready
                            ? t("share_ready", { at: test.approved_at })
                            : t("share_not_ready")}
                        </div>
                      </div>
                    </div>
                    {test.ready && test.report && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === test.report}
                        onClick={() =>
                          download("report", test.report ?? "", test.title)
                        }
                        data-cy="share-download"
                      >
                        <Download className="size-4" />
                        {t("download")}
                      </Button>
                    )}
                  </div>
                ))}
                {unlocked.bill && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy === "bill"}
                    onClick={() =>
                      download("bill", undefined, `bill-${unlocked.bill?.number}`)
                    }
                  >
                    <Receipt className="size-4" />
                    {t("share_download_bill", { number: unlocked.bill.number })}
                  </Button>
                )}
                {error && <p className="text-sm text-red-700">{error}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
